import React, { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import './graph.css';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { drawNodePhoto, getColor } from './utils';
import PersonDrawer from './PersonDrawer/PersonDrawer';
import { EdgeDrawer } from './EdgeDrawer/EdgeDrawer';
import { mapPersonToEdges } from './utils';
import { forceCollide } from 'd3-force-3d';
import { getNonOriginalGraphEdges, getOriginalGraphEdges } from '../graphEdges';
import {
  GRAPH_CENTER_STRENGTH,
  GRAPH_CHARGE_STRENGTH,
  GRAPH_COLLISION_ITERATIONS,
  GRAPH_COLLISION_RADIUS,
  GRAPH_COLLISION_STRENGTH,
  GRAPH_LINK_DISTANCE,
  GRAPH_NODE_SIZE,
} from '../graphLayout';
import { getGraphNameParam } from '../graphRoutes';
import { getPersonPhotoImage, preloadPersonPhotoImages, prunePersonPhotoCache } from '../graphPhotos';
import { getEdgeCategoryOptions, getEdgeCategoryColor } from '../edgeCategories';
import { getGraphTypeLabel, normalizeGraphType } from '../graphTypes';
import { analyzeGraph, shortestPath, simulateRemoval, sortEdgesByDate } from '../Graph/graphAnalytics';
import { CloudinaryUpload } from '../CloudinaryUpload';
import { getStoredEditorToken, isValidEditorToken } from '../Auth/GoogleEditorGate';
import CloseIcon from '@mui/icons-material/Close';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import StopIcon from '@mui/icons-material/Stop';
import ShareIcon from '@mui/icons-material/Share';

const PEOPLE_QUERY = gql`
  query People($graphName: String) {
    graph(graphName: $graphName) {
      _id
      name
      slug
      type
      isDefault
      focusPersonId
    }
    people(graphName: $graphName) {
      _id
      graphId
      name
      imageURL
      funFact
      instagram
      birthDate
      deathDate
      locationX
      locationY
    }
    edges(graphName: $graphName) {
        _id
        graphId
        person1
        person2
        notes
        category
        dateMet
        images {
            imageURL
            caption
        }
        interviewQuestions {
            question
            answer
            personId
        }
    }
    personStories(graphName: $graphName) {
      _id
      graphId
      personId
      title
      story
      storyDate
      taggedPersonIds
      submitterName
      createdOn
    }
  }
`;

const SUBMIT_GRAPH_SUGGESTION_MUTATION = gql`
  mutation SubmitGraphSuggestion($input: GraphSuggestionInput!) {
    submitGraphSuggestion(input: $input) {
      _id
      status
    }
  }
`;

function stableCoordinate(id, axis) {
  const input = `${id || ''}-${axis}`;
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0;
  }
  return (Math.abs(hash) % 320) - 160;
}

function getDisjointTreeCountsByStep(people, edges) {
  const parent = new Map();

  people.forEach((person) => {
    parent.set(String(person._id), String(person._id));
  });

  let treeCount = parent.size;

  const find = (id) => {
    const currentParent = parent.get(id);
    if (!currentParent) return id;
    if (currentParent === id) return id;
    const root = find(currentParent);
    parent.set(id, root);
    return root;
  };

  const union = (firstId, secondId) => {
    if (!parent.has(firstId) || !parent.has(secondId)) return;
    const firstRoot = find(firstId);
    const secondRoot = find(secondId);
    if (firstRoot !== secondRoot) {
      parent.set(secondRoot, firstRoot);
      treeCount -= 1;
    }
  };

  return edges.map((edge) => {
    union(String(edge.person1), String(edge.person2));
    return treeCount;
  });
}

function edgeConnects(edge, firstPersonId, secondPersonId) {
  return (edge.person1 === firstPersonId && edge.person2 === secondPersonId)
    || (edge.person1 === secondPersonId && edge.person2 === firstPersonId);
}

function getPathEdges(pathPersonIds, edges) {
  if (!pathPersonIds.length) return [];
  return pathPersonIds.slice(0, -1).map((personId, index) => (
    edges.find((edge) => edgeConnects(edge, personId, pathPersonIds[index + 1]))
  )).filter(Boolean);
}

function sortOptionsByLabel(options) {
  return [...options].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

function SearchableSelect({ label, options, value, onChange, placeholder = 'Search...' }) {
  const sortedOptions = useMemo(() => sortOptionsByLabel(options), [options]);
  const selectedOption = sortedOptions.find((option) => option.value === value) || null;
  const [query, setQuery] = useState(selectedOption?.label || '');
  const [isOpen, setIsOpen] = useState(false);
  const blurTimerRef = useRef(null);
  const listboxIdRef = useRef(`read-search-select-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!isOpen) {
      setQuery(selectedOption?.label || '');
    }
  }, [isOpen, selectedOption?.label]);

  useEffect(() => () => {
    if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
  }, []);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return sortedOptions;
    return sortedOptions.filter((option) => option.label.toLowerCase().includes(normalizedQuery));
  }, [query, sortedOptions]);

  const chooseOption = (option) => {
    onChange(option.value);
    setQuery(option.label);
    setIsOpen(false);
  };

  const openSelector = () => {
    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setQuery((current) => (
      selectedOption?.label && current === selectedOption.label ? '' : current
    ));
    setIsOpen(true);
  };

  const settleInput = () => {
    blurTimerRef.current = window.setTimeout(() => {
      const exactMatch = sortedOptions.find((option) => option.label.toLowerCase() === query.trim().toLowerCase());
      if (exactMatch) {
        chooseOption(exactMatch);
      } else {
        setQuery(selectedOption?.label || '');
        setIsOpen(false);
      }
    }, 120);
  };

  return (
    <label className="read-search-select">
      <span>{label}</span>
      <input
        className="read-search-select-input"
        value={query}
        onBlur={settleInput}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={openSelector}
        placeholder={placeholder}
        role="combobox"
        aria-controls={listboxIdRef.current}
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-autocomplete="list"
      />
      {isOpen && (
        <div id={listboxIdRef.current} className="read-search-select-menu" role="listbox">
          {filteredOptions.length ? filteredOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`read-search-select-option${option.value === value ? ' read-search-select-option--selected' : ''}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => chooseOption(option)}
              role="option"
              aria-selected={option.value === value}
            >
              {option.label}
            </button>
          )) : (
            <div className="read-search-select-empty">No matches</div>
          )}
        </div>
      )}
    </label>
  );
}

export default function GraphRead() {
  const params = useParams();
  const graphName = getGraphNameParam(params);
  const { data: graphData, loading, error, refetch: refetchGraph } = useQuery(PEOPLE_QUERY, {
    variables: { graphName },
  });
  const [submitGraphSuggestion, { loading: isSubmittingSuggestion }] = useMutation(SUBMIT_GRAPH_SUGGESTION_MUTATION);

  const [data, setData] = useState({ nodes: [], links: [] });
  const [graphSize, setGraphSize] = useState(() => ({
    width: typeof window === 'undefined' ? 1280 : window.innerWidth,
    height: typeof window === 'undefined' ? 720 : window.innerHeight,
  }));

  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [idToNode] = useState({});
  const [activeReadTab, setActiveReadTab] = useState('graph');
  const [isEditorSubmitting, setIsEditorSubmitting] = useState(() => isValidEditorToken(getStoredEditorToken()));
  const [pathStartPersonId, setPathStartPersonId] = useState('');
  const [pathEndPersonId, setPathEndPersonId] = useState('');
  const [whatIfPersonId, setWhatIfPersonId] = useState('');
  const [suggestionForm, setSuggestionForm] = useState({
    suggestedPersonName: '',
    suggestedPersonImageURL: '',
    connectToPersonId: '',
    dateMet: '',
    category: 'friendship',
    notes: '',
    submitterName: '',
    submitterEmail: '',
    submitterNote: '',
  });
  const [suggestionStatus, setSuggestionStatus] = useState('');
  const [suggestionError, setSuggestionError] = useState('');
  const [suggestionImageFailed, setSuggestionImageFailed] = useState(false);
  const [activeInsightMode, setActiveInsightMode] = useState(null);
  const [insightStep, setInsightStep] = useState(0);
  const [selectedSuperlativeId, setSelectedSuperlativeId] = useState('');
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [isTimelineClosing, setIsTimelineClosing] = useState(false);
  const [isTimelinePlaying, setIsTimelinePlaying] = useState(false);
  const [timelineStep, setTimelineStep] = useState(0);

  const graphRef = useRef(null);
  const refreshFrameRef = useRef(null);
  const insightAnimationFrameRef = useRef(null);
  const timelineLayoutRef = useRef(new Map());
  const timelineListRef = useRef(null);
  const insightListRef = useRef(null);
  const timelineCloseTimerRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.add('graph-read-scroll-lock');
    document.body.classList.add('graph-read-scroll-lock');
    return () => {
      document.documentElement.classList.remove('graph-read-scroll-lock');
      document.body.classList.remove('graph-read-scroll-lock');
    };
  }, []);

  useEffect(() => {
    const refreshEditorState = () => {
      setIsEditorSubmitting(isValidEditorToken(getStoredEditorToken()));
    };

    refreshEditorState();
    window.addEventListener('storage', refreshEditorState);
    window.addEventListener('focus', refreshEditorState);
    return () => {
      window.removeEventListener('storage', refreshEditorState);
      window.removeEventListener('focus', refreshEditorState);
    };
  }, []);

  const scheduleGraphRefresh = useCallback(() => {
    if (refreshFrameRef.current) return;
    refreshFrameRef.current = window.requestAnimationFrame(() => {
      refreshFrameRef.current = null;
      graphRef.current?.refresh?.();
    });
  }, []);

  const applyGraphPhysics = useCallback((reheat = false) => {
    if (!graphRef.current) return;
    graphRef.current.d3Force?.('charge')?.strength?.(GRAPH_CHARGE_STRENGTH);
    graphRef.current.d3Force?.('link')?.distance?.(GRAPH_LINK_DISTANCE);
    graphRef.current.d3Force?.('center')?.strength?.(GRAPH_CENTER_STRENGTH);
    graphRef.current.d3Force?.(
      'collide',
      forceCollide(GRAPH_COLLISION_RADIUS)
        .strength(GRAPH_COLLISION_STRENGTH)
        .iterations(GRAPH_COLLISION_ITERATIONS)
    );
    if (reheat) graphRef.current.d3ReheatSimulation?.();
  }, []);

  const handleNodeClick = (node) => {
    setSelectedEdge(null);
    setSelectedNode(node);
    graphRef.current?.centerAt?.(node.x, node.y, 420);
    graphRef.current?.zoom?.(window.innerWidth < 680 ? 2.1 : 2.6, 420);
    setOpen(true);
  };

  const handleEdgeClick = (edge) => {
    setSelectedNode(null);
    setSelectedEdge(edge);
    const source = edge.source;
    const target = edge.target;
    if (source && target && typeof source.x === 'number' && typeof target.x === 'number') {
      graphRef.current?.centerAt?.((source.x + target.x) / 2, (source.y + target.y) / 2, 420);
      graphRef.current?.zoom?.(window.innerWidth < 680 ? 1.9 : 2.35, 420);
    }
    setOpen(true);
  };

  const idToPerson = useMemo(() => {
    if (!graphData) return {};
    const { people } = graphData;
    return people.reduce((acc, person) => {
      acc[person._id] = person;
      return acc;
    }, {});
  }, [graphData]);

  const formatDate = (value) => {
    if (!value) return 'No date';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'No date' : date.toLocaleDateString();
  };

  const formatYear = useCallback((value) => {
    if (!value) return 'unknown';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'unknown' : String(date.getFullYear());
  }, []);

  const getEdgePersonLabel = useCallback((edge) => {
    return `${idToPerson[edge?.person1]?.name || 'Person'} + ${idToPerson[edge?.person2]?.name || 'Person'}`;
  }, [idToPerson]);

  const getPersonName = useCallback((personId) => {
    return idToPerson[personId]?.name || 'Unknown person';
  }, [idToPerson]);

  const getFriendshipLabel = useCallback((edge) => {
    if (!edge) return 'No dated friendship yet';
    return `${getPersonName(edge.person1)} + ${getPersonName(edge.person2)} (${formatDate(edge.dateMet)})`;
  }, [getPersonName]);

  const smallWorldEdges = useMemo(() => {
    return getNonOriginalGraphEdges(graphData?.edges || [])
      .sort((a, b) => new Date(b.dateMet || 0).getTime() - new Date(a.dateMet || 0).getTime());
  }, [graphData]);

  const analyticsPeople = useMemo(() => graphData?.people || [], [graphData]);
  const analyticsEdges = useMemo(() => graphData?.edges || [], [graphData]);
  const graphType = normalizeGraphType(graphData?.graph?.type);
  const isGraphNotFound = Boolean(error?.message?.includes('Graph not found'));
  const storiesByPerson = useMemo(() => (
    (graphData?.personStories || []).reduce((acc, story) => {
      const personId = story.personId;
      acc[personId] = [...(acc[personId] || []), story];
      return acc;
    }, {})
  ), [graphData?.personStories]);
  const sortedAnalyticsPeople = useMemo(() => (
    [...analyticsPeople].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  ), [analyticsPeople]);
  const personSelectOptions = useMemo(() => (
    sortedAnalyticsPeople.map((person) => ({ value: person._id, label: person.name || 'Unknown person' }))
  ), [sortedAnalyticsPeople]);
  const categorySelectOptions = useMemo(() => (
    sortOptionsByLabel(getEdgeCategoryOptions(graphType).map((option) => ({ value: option.value, label: option.label })))
  ), [graphType]);

  useEffect(() => {
    setSuggestionForm((current) => (
      categorySelectOptions.some((option) => option.value === current.category)
        ? current
        : {...current, category: categorySelectOptions[0]?.value || 'friendship'}
    ));
  }, [categorySelectOptions]);
  const graphAnalytics = useMemo(() => (
    analyzeGraph(analyticsPeople, analyticsEdges)
  ), [analyticsEdges, analyticsPeople]);
  const superlativeCards = useMemo(() => {
    const cards = [];
    const addPersonCard = ({ id, title, teaser, personId, answer, detail, accent = '#00d5ff' }) => {
      if (!personId) return;
      cards.push({
        id,
        type: 'person',
        title,
        teaser,
        answer,
        detail,
        accent,
        personIds: [personId],
        edgeIds: [],
        edges: [],
      });
    };
    const addEdgeCard = ({ id, title, teaser, edge, answer, detail, accent = '#00d5ff' }) => {
      if (!edge) return;
      cards.push({
        id,
        type: 'edge',
        title,
        teaser,
        answer,
        detail,
        accent,
        personIds: [edge.person1, edge.person2].filter(Boolean),
        edgeIds: [edge._id],
        edges: [edge],
      });
    };

    if (graphType === 'family') {
      addPersonCard({
        id: 'oldest-ancestor',
        title: 'Oldest ancestor',
        teaser: 'Who goes furthest back?',
        personId: graphAnalytics.family.oldestPerson?._id,
        answer: `${getPersonName(graphAnalytics.family.oldestPerson?._id)} was born in ${formatYear(graphAnalytics.family.oldestPerson?.birthDate)}.`,
        detail: 'Based on the earliest birth date in the tree.',
        accent: '#32ff6a',
      });
      addPersonCard({
        id: 'longest-life',
        title: 'Longest life',
        teaser: 'Whose story spans the most years?',
        personId: graphAnalytics.family.longestLife?.personId,
        answer: `${getPersonName(graphAnalytics.family.longestLife?.personId)} spans about ${Math.round(graphAnalytics.family.longestLife?.years || 0)} years.`,
        detail: 'Uses birth and death dates; living people count through today.',
        accent: '#ffd60a',
      });
      addPersonCard({
        id: 'most-children',
        title: 'Most child connections',
        teaser: 'Who anchors the biggest child branch?',
        personId: graphAnalytics.family.mostChildConnections?.personId,
        answer: `${getPersonName(graphAnalytics.family.mostChildConnections?.personId)} has ${graphAnalytics.family.mostChildConnections?.count || 0} child connection${graphAnalytics.family.mostChildConnections?.count === 1 ? '' : 's'}.`,
        detail: 'Counts Child-category family-tree links.',
        accent: '#00d5ff',
      });
      addEdgeCard({
        id: 'oldest-marriage',
        title: 'Oldest marriage or partnership',
        teaser: 'Which partnership goes back furthest?',
        edge: graphAnalytics.family.oldestMarriage,
        answer: getFriendshipLabel(graphAnalytics.family.oldestMarriage),
        detail: graphAnalytics.family.oldestMarriage?.notes || 'Earliest dated Married or Partner edge.',
        accent: '#ff2d95',
      });
      addEdgeCard({
        id: 'newest-sibling',
        title: 'Newest sibling bond',
        teaser: 'Which sibling connection was added most recently?',
        edge: graphAnalytics.family.mostRecentSiblingBond,
        answer: getFriendshipLabel(graphAnalytics.family.mostRecentSiblingBond),
        detail: graphAnalytics.family.mostRecentSiblingBond?.notes || 'Latest dated Sibling edge.',
        accent: '#8b5cf6',
      });
      return cards;
    }

    if (graphType === 'wedding') {
      addEdgeCard({
        id: 'wedding-couple',
        title: 'The couple',
        teaser: 'Who is at the center of the day?',
        edge: graphAnalytics.wedding.coupleEdge,
        answer: getFriendshipLabel(graphAnalytics.wedding.coupleEdge),
        detail: graphAnalytics.wedding.coupleEdge?.notes || 'Use the Couple/Married category to identify the couple.',
        accent: '#ffd60a',
      });
      addPersonCard({
        id: 'wedding-party-mvp',
        title: 'Wedding party connector',
        teaser: 'Who bridges the wedding party?',
        personId: graphAnalytics.wedding.weddingPartyMvp?.personId,
        answer: `${getPersonName(graphAnalytics.wedding.weddingPartyMvp?.personId)} is most central among wedding-party connections.`,
        detail: `${graphAnalytics.wedding.weddingPartyCount || 0} people appear in wedding-party links.`,
        accent: '#ff2d95',
      });
      addPersonCard({
        id: 'guest-connector',
        title: 'Guest connector',
        teaser: 'Which guest connects the room?',
        personId: graphAnalytics.wedding.mostConnectedGuest?.personId,
        answer: `${getPersonName(graphAnalytics.wedding.mostConnectedGuest?.personId)} is most central among guest connections.`,
        detail: 'Based on Guest-category links.',
        accent: '#00d5ff',
      });
      addEdgeCard({
        id: 'oldest-family-bond',
        title: 'Oldest family bond',
        teaser: 'Which family connection goes back furthest?',
        edge: graphAnalytics.wedding.oldestFamilyBond,
        answer: getFriendshipLabel(graphAnalytics.wedding.oldestFamilyBond),
        detail: graphAnalytics.wedding.oldestFamilyBond?.notes || 'Earliest dated Family edge.',
        accent: '#32ff6a',
      });
      return cards;
    }

    addPersonCard({
      id: 'most-influential',
      title: 'Most influential connector',
      teaser: 'Who brought the most people in?',
      personId: graphAnalytics.mostInfluential?.personId,
      answer: `${getPersonName(graphAnalytics.mostInfluential?.personId)} introduced ${graphAnalytics.mostInfluential?.count || 0} people.`,
      detail: 'Their node pulses while the rest of the map dims.',
      accent: '#00d5ff',
    });
    addPersonCard({
      id: 'most-central',
      title: 'Most central person',
      teaser: 'Who sits closest to everyone?',
      personId: graphAnalytics.mostCentral?.personId,
      answer: `${getPersonName(graphAnalytics.mostCentral?.personId)} has the shortest average path through the group.`,
      detail: graphAnalytics.mostCentral?.averageDistance
        ? `${graphAnalytics.mostCentral.averageDistance.toFixed(2)} average steps from reachable people.`
        : 'Centrality is based on shortest paths.',
      accent: '#ff2d95',
    });
    addPersonCard({
      id: 'largest-chain',
      title: 'Largest recruitment chain',
      teaser: 'Whose branch grew the farthest?',
      personId: graphAnalytics.largestRecruitmentChain?.personId,
      answer: `${getPersonName(graphAnalytics.largestRecruitmentChain?.personId)} anchors a chain of ${graphAnalytics.largestRecruitmentChain?.count || 0}.`,
      detail: 'Measured from dated relationship order.',
      accent: '#ffd60a',
    });
    addEdgeCard({
      id: 'oldest-non-family',
      title: 'Oldest bond (non-family)',
      teaser: 'Which non-family bond goes back furthest?',
      edge: graphAnalytics.oldestNonFamilyBond,
      answer: getFriendshipLabel(graphAnalytics.oldestNonFamilyBond),
      detail: graphAnalytics.oldestNonFamilyBond?.notes || 'Earliest dated non-family connection.',
      accent: '#32ff6a',
    });
    addEdgeCard({
      id: 'most-recent',
      title: 'Most recent friendship',
      teaser: 'What is the newest bond?',
      edge: graphAnalytics.mostRecentFriendship,
      answer: getFriendshipLabel(graphAnalytics.mostRecentFriendship),
      detail: graphAnalytics.mostRecentFriendship?.notes || 'Most recent dated connection.',
      accent: '#8b5cf6',
    });
    addEdgeCard({
      id: 'worst-breakup',
      title: 'Couple whose breakup would be the worst',
      teaser: 'Which split would shake the map most?',
      edge: graphAnalytics.worstBreakupCouple?.edge,
      answer: graphAnalytics.worstBreakupCouple?.edge
        ? getFriendshipLabel(graphAnalytics.worstBreakupCouple.edge)
        : 'No dating couple found yet.',
      detail: graphAnalytics.worstBreakupCouple?.componentIncrease
        ? `The map gains ${graphAnalytics.worstBreakupCouple.componentIncrease} extra group${graphAnalytics.worstBreakupCouple.componentIncrease === 1 ? '' : 's'} if this bond disappears.`
        : 'Ranked by map fragmentation, then combined connectedness.',
      accent: '#ff3b5f',
    });
    if (!graphAnalytics.worstBreakupCouple?.edge) {
      cards.push({
        id: 'worst-breakup',
        type: 'empty',
        title: 'Couple whose breakup would be the worst',
        teaser: 'Which split would shake the map most?',
        answer: 'No dating couple found yet.',
        detail: 'Add dating-category bonds to unlock this superlative.',
        accent: '#ff3b5f',
        personIds: [],
        edgeIds: [],
        edges: [],
      });
    }

    return cards;
  }, [formatYear, getFriendshipLabel, getPersonName, graphAnalytics, graphType]);
  const selectedSuperlativeCard = useMemo(() => (
    superlativeCards.find((card) => card.id === selectedSuperlativeId) || superlativeCards[0] || null
  ), [selectedSuperlativeId, superlativeCards]);
  const selectedSuperlativePersonIds = useMemo(() => (
    new Set(activeInsightMode === 'superlative' ? selectedSuperlativeCard?.personIds || [] : [])
  ), [activeInsightMode, selectedSuperlativeCard]);
  const selectedSuperlativeEdgeIds = useMemo(() => (
    new Set(activeInsightMode === 'superlative' ? selectedSuperlativeCard?.edgeIds || [] : [])
  ), [activeInsightMode, selectedSuperlativeCard]);
  const timelineEdges = useMemo(() => (
    sortEdgesByDate(getOriginalGraphEdges(graphData?.edges || []))
  ), [graphData?.edges]);
  const timelineEdgeCount = Math.min(timelineStep, timelineEdges.length);
  const isTimelineActive = isTimelineOpen || isTimelineClosing || isTimelinePlaying || timelineEdgeCount > 0;
  const visibleTimelineEdges = useMemo(() => (
    timelineEdges.slice(0, timelineEdgeCount)
  ), [timelineEdgeCount, timelineEdges]);
  const timelineTreeCountsByStep = useMemo(() => (
    getDisjointTreeCountsByStep(analyticsPeople, timelineEdges)
  ), [analyticsPeople, timelineEdges]);
  const timelineTreeCount = timelineEdgeCount > 0
    ? timelineTreeCountsByStep[timelineEdgeCount - 1] || analyticsPeople.length
    : analyticsPeople.length;
  const currentTimelineEdge = timelineEdgeCount > 0 ? timelineEdges[timelineEdgeCount - 1] : null;
  const timelineDisplayedData = useMemo(() => {
    if (!isTimelineActive || !timelineEdges.length) return data;

    return {
      nodes: data.nodes.map((node) => {
        const savedPosition = timelineLayoutRef.current.get(node.id);
        const x = savedPosition?.x ?? node.x ?? node.locationX ?? stableCoordinate(node.id, 'x');
        const y = savedPosition?.y ?? node.y ?? node.locationY ?? stableCoordinate(node.id, 'y');
        return {
          ...node,
          x,
          y,
          fx: x,
          fy: y,
        };
      }),
      links: visibleTimelineEdges.map((edge) => ({
        ...edge,
        source: edge.person1,
        target: edge.person2,
        note: edge.notes,
        dateMet: edge.dateMet,
        id: edge._id,
      })),
    };
  }, [data, isTimelineActive, timelineEdges.length, visibleTimelineEdges]);
  const pathPersonIds = useMemo(() => (
    shortestPath(analyticsPeople, analyticsEdges, pathStartPersonId, pathEndPersonId)
  ), [analyticsEdges, analyticsPeople, pathEndPersonId, pathStartPersonId]);
  const pathEdges = useMemo(() => (
    getPathEdges(pathPersonIds, analyticsEdges)
  ), [analyticsEdges, pathPersonIds]);
  const originalEdgeIds = useMemo(() => (
    new Set(getOriginalGraphEdges(analyticsEdges).map((edge) => edge._id))
  ), [analyticsEdges]);
  const smallWorldPathEdgeIds = useMemo(() => (
    new Set(pathEdges
      .filter((edge) => edge?._id && !originalEdgeIds.has(edge._id))
      .map((edge) => edge._id))
  ), [originalEdgeIds, pathEdges]);
  const pathUsesSmallWorldConnection = smallWorldPathEdgeIds.size > 0;
  const whatIfResult = useMemo(() => (
    simulateRemoval(analyticsPeople, analyticsEdges, whatIfPersonId)
  ), [analyticsEdges, analyticsPeople, whatIfPersonId]);
  const whatIfOriginalOnlyResult = useMemo(() => (
    simulateRemoval(analyticsPeople, getOriginalGraphEdges(analyticsEdges), whatIfPersonId)
  ), [analyticsEdges, analyticsPeople, whatIfPersonId]);
  const whatIfSmallWorldRescue = useMemo(() => {
    if (!whatIfResult || !whatIfOriginalOnlyResult) return null;
    const savedGroupCount = whatIfOriginalOnlyResult.componentCount - whatIfResult.componentCount;
    if (savedGroupCount <= 0) return null;
    return {
      savedGroupCount,
      originalOnlyComponentCount: whatIfOriginalOnlyResult.componentCount,
      actualComponentCount: whatIfResult.componentCount,
    };
  }, [whatIfOriginalOnlyResult, whatIfResult]);
  const isPathInsightActive = activeReadTab === 'graph' && activeInsightMode === 'path';
  const isWhatIfInsightActive = activeReadTab === 'graph' && activeInsightMode === 'whatIf';
  const isSuperlativeInsightActive = activeReadTab === 'graph' && activeInsightMode === 'superlative';
  const isInsightVisualActive = isPathInsightActive || isWhatIfInsightActive || isSuperlativeInsightActive;
  const visiblePathNodeIds = useMemo(() => (
    new Set(isPathInsightActive ? pathPersonIds.slice(0, Math.min(pathPersonIds.length, insightStep + 1)) : [])
  ), [insightStep, isPathInsightActive, pathPersonIds]);
  const visiblePathEdgeIds = useMemo(() => (
    new Set(isPathInsightActive ? pathEdges.slice(0, insightStep).map((edge) => edge._id) : [])
  ), [insightStep, isPathInsightActive, pathEdges]);
  const pathNodeIds = useMemo(() => new Set(isPathInsightActive ? pathPersonIds : []), [isPathInsightActive, pathPersonIds]);
  const pathEdgeIds = useMemo(() => new Set(isPathInsightActive ? pathEdges.map((edge) => edge._id) : []), [isPathInsightActive, pathEdges]);
  const whatIfComponentByPersonId = useMemo(() => {
    const componentByPersonId = new Map();
    (whatIfResult?.components || []).forEach((component, componentIndex) => {
      component.forEach((personId) => componentByPersonId.set(personId, componentIndex));
    });
    return componentByPersonId;
  }, [whatIfResult]);
  const whatIfIncidentEdgeIds = useMemo(() => (
    new Set((analyticsEdges || [])
      .filter((edge) => edge.person1 === whatIfPersonId || edge.person2 === whatIfPersonId)
      .map((edge) => edge._id))
  ), [analyticsEdges, whatIfPersonId]);
  const whatIfStorySteps = useMemo(() => {
    const personName = getPersonName(whatIfPersonId);
    const directBondCount = whatIfIncidentEdgeIds.size;
    const groupCount = whatIfResult?.componentCount || 0;
    const largestGroupSize = whatIfResult?.largestComponentSize || 0;
    const splitLabel = groupCount === 1 ? 'one connected group' : `${groupCount} separate groups`;
    const largestLabel = largestGroupSize === 1 ? '1 person' : `${largestGroupSize} people`;
    const rescueDetail = whatIfSmallWorldRescue
      ? `But the story bends: small-world connections keep ${whatIfSmallWorldRescue.savedGroupCount} potential split${whatIfSmallWorldRescue.savedGroupCount === 1 ? '' : 's'} together.`
      : 'No shortcut bond steps in, so the remaining shape tells the whole story.';

    return [
      {
        title: `Find ${personName}`,
        detail: 'The map starts by locating the person whose absence we are about to imagine.',
        tone: 'danger',
      },
      {
        title: 'Lift out their direct bonds',
        detail: `${directBondCount || 'Their'} direct connection${directBondCount === 1 ? '' : 's'} fade first, so you can see the immediate ripple.`,
        tone: 'danger',
      },
      {
        title: 'Watch the group re-form',
        detail: whatIfResult
          ? `Without ${personName}, the map settles into ${splitLabel}; the largest remaining group has ${largestLabel}.`
          : 'The map checks which people can still reach each other.',
        tone: 'reveal',
      },
      {
        title: whatIfSmallWorldRescue ? 'A shortcut saves part of the group' : 'No hidden shortcut appears',
        detail: rescueDetail,
        tone: whatIfSmallWorldRescue ? 'rescue' : 'reveal',
      },
      {
        title: groupCount <= 1 ? 'The group holds together' : 'The map shows the fracture',
        detail: groupCount <= 1
          ? `${personName} matters, but the rest of the map still has enough paths to stay connected.`
          : `${personName} is a bridge here: their absence leaves visible islands in the network.`,
        tone: groupCount <= 1 ? 'rescue' : 'danger',
      },
    ];
  }, [getPersonName, whatIfIncidentEdgeIds.size, whatIfPersonId, whatIfResult, whatIfSmallWorldRescue]);
  const displayedData = useMemo(() => {
    if (isTimelineActive) return timelineDisplayedData;
    if (!isPathInsightActive && !isSuperlativeInsightActive) return data;

    const existingLinkIds = new Set(data.links.map((link) => link.id || link._id));
    const visualEdges = isPathInsightActive ? pathEdges : selectedSuperlativeCard?.edges || [];
    const additionalPathLinks = visualEdges
      .filter((edge) => !existingLinkIds.has(edge._id))
      .map((edge) => ({
        ...edge,
        source: edge.person1,
        target: edge.person2,
        note: edge.notes,
        dateMet: edge.dateMet,
        id: edge._id,
      }));

    return {
      nodes: data.nodes,
      links: [...data.links, ...additionalPathLinks],
    };
  }, [data, isPathInsightActive, isSuperlativeInsightActive, isTimelineActive, pathEdges, selectedSuperlativeCard, timelineDisplayedData]);

  const [personToEdges, setPersonToEdges] = useState({});

  useEffect(() => {
    const updateSize = () => {
      setGraphSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    window.addEventListener('orientationchange', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('orientationchange', updateSize);
    };
  }, []);

  useEffect(() => {
    if (!graphData) return;

    const { people, edges } = graphData;
    const p2E = mapPersonToEdges(people,edges)
    setPersonToEdges(p2E);
    const nodes = people.map((user) => {
      const hasSavedLocation = Number.isFinite(user.locationX) && Number.isFinite(user.locationY);
      const x = hasSavedLocation ? user.locationX : stableCoordinate(user._id, 'x');
      const y = hasSavedLocation ? user.locationY : stableCoordinate(user._id, 'y');
      return {
        ...user,
        id: user._id,
        name: user.name,
        image: user.imageURL || `https://robohash.org/${user.name}`,
        x,
        y,
      };
    });

    const links = getOriginalGraphEdges(edges).map((edge) => ({
        ...edge,
        source: edge.person1,
        target: edge.person2,
        note: edge.notes,
        dateMet: edge.dateMet,
        id: edge._id,
    }));

    setData({ nodes, links });
  }, [graphData]);

  useEffect(() => {
    applyGraphPhysics(true);
  }, [applyGraphPhysics, data.nodes.length, data.links.length]);


  const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
    if (!newOpen) {
      setTimeout(() => {
        setSelectedEdge(null);
        setSelectedNode(null);
      },200);
    }
  };

  const [firstZoom, setFirstZoom] = useState(true);
  useEffect(() => {
    setFirstZoom(true);
    setSelectedNode(null);
    setSelectedEdge(null);
    setOpen(false);
    setIsTimelineOpen(false);
    setIsTimelineClosing(false);
    setIsTimelinePlaying(false);
    setTimelineStep(0);
    setActiveInsightMode(null);
    setInsightStep(0);
    setSelectedSuperlativeId('');
    setActiveReadTab('graph');
  }, [graphName]);

  useEffect(() => {
    if (!superlativeCards.length) {
      setSelectedSuperlativeId('');
      return;
    }
    setSelectedSuperlativeId((current) => (
      current && superlativeCards.some((card) => card.id === current)
        ? current
        : superlativeCards[0].id
    ));
  }, [superlativeCards]);

  useEffect(() => {
    if (!analyticsPeople.length) {
      setPathStartPersonId('');
      setPathEndPersonId('');
      setWhatIfPersonId('');
      return;
    }

    setPathStartPersonId((current) => current && analyticsPeople.some((person) => person._id === current) ? current : sortedAnalyticsPeople[0]._id);
    setPathEndPersonId((current) => current && analyticsPeople.some((person) => person._id === current) ? current : sortedAnalyticsPeople[1]?._id || sortedAnalyticsPeople[0]._id);
    setWhatIfPersonId((current) => current && analyticsPeople.some((person) => person._id === current) ? current : sortedAnalyticsPeople[0]._id);
    setSuggestionForm((current) => ({
      ...current,
      connectToPersonId: current.connectToPersonId && analyticsPeople.some((person) => person._id === current.connectToPersonId)
        ? current.connectToPersonId
        : sortedAnalyticsPeople[0]._id,
    }));
  }, [analyticsPeople, sortedAnalyticsPeople]);

  useEffect(() => {
    if (!activeInsightMode) return undefined;
    setInsightStep(0);
    const maxStep = activeInsightMode === 'path'
      ? Math.max(0, pathPersonIds.length - 1)
      : activeInsightMode === 'superlative'
        ? 2
        : Math.max(0, whatIfStorySteps.length - 1);
    const intervalId = window.setInterval(() => {
      setInsightStep((current) => {
        if (current >= maxStep) {
          window.clearInterval(intervalId);
          return current;
        }
        return current + 1;
      });
    }, 680);

    return () => window.clearInterval(intervalId);
  }, [activeInsightMode, pathPersonIds.length, pathStartPersonId, pathEndPersonId, selectedSuperlativeId, whatIfPersonId, whatIfStorySteps.length]);

  useEffect(() => {
    if (!activeInsightMode) {
      if (insightAnimationFrameRef.current) {
        window.cancelAnimationFrame(insightAnimationFrameRef.current);
        insightAnimationFrameRef.current = null;
      }
      return undefined;
    }

    const refresh = () => {
      graphRef.current?.refresh?.();
      insightAnimationFrameRef.current = window.requestAnimationFrame(refresh);
    };
    insightAnimationFrameRef.current = window.requestAnimationFrame(refresh);

    return () => {
      if (insightAnimationFrameRef.current) {
        window.cancelAnimationFrame(insightAnimationFrameRef.current);
        insightAnimationFrameRef.current = null;
      }
    };
  }, [activeInsightMode]);

  useEffect(() => {
    if (!isTimelinePlaying) return undefined;
    if (!timelineEdges.length) {
      setIsTimelinePlaying(false);
      setTimelineStep(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setTimelineStep((current) => {
        if (current >= timelineEdges.length) {
          setIsTimelinePlaying(false);
          return current;
        }
        return current + 1;
      });
    }, 900);

    return () => window.clearInterval(intervalId);
  }, [isTimelinePlaying, timelineEdges.length]);

  useEffect(() => {
    if (!isTimelineActive || !timelineListRef.current) return;
    timelineListRef.current.scrollTo({
      top: timelineListRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [isTimelineActive, timelineEdgeCount]);

  useEffect(() => {
    if ((!isPathInsightActive && !isWhatIfInsightActive) || !insightListRef.current) return;
    const activeSteps = insightListRef.current.querySelectorAll('.read-insight-visual-step--active');
    const activeStep = activeSteps[activeSteps.length - 1];
    if (!activeStep) return;
    activeStep.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [insightStep, isPathInsightActive, isWhatIfInsightActive]);

  useEffect(() => {
    prunePersonPhotoCache(graphData?.people || []);
    preloadPersonPhotoImages(graphData?.people || [], scheduleGraphRefresh);
  }, [graphData?.people, scheduleGraphRefresh]);

  useEffect(() => () => {
    if (refreshFrameRef.current) {
      window.cancelAnimationFrame(refreshFrameRef.current);
    }
  }, []);

  const handleEngineStop = () => {
    if (isTimelineActive) {
      graphRef.current?.zoomToFit?.(500, graphSize.width > 760 ? 96 : 44);
      setFirstZoom(false);
      return;
    }
    applyGraphPhysics(false);
    const d3Zoom = graphRef.current?.zoom;
    const focusPerson = graphData?.people?.find((person) => person._id === graphData?.graph?.focusPersonId)
      || graphData?.people?.[0];
    if (d3Zoom && focusPerson && firstZoom) {
      const firstNode = data.nodes.find((node) => node.id === focusPerson._id);
      graphRef.current.centerAt(
        firstNode?.x ?? focusPerson.locationX ?? stableCoordinate(focusPerson._id, 'x'),
        firstNode?.y ?? focusPerson.locationY ?? stableCoordinate(focusPerson._id, 'y'),
        500
      )
      setFirstZoom(false);
      setTimeout(() => {
        d3Zoom(window.innerWidth < 680 ? 2.35 : 3.1, 500);
    }, 500);
    }
  };

  const openSmallWorldEdge = (edge) => {
    const source = data.nodes.find((node) => node.id === edge.person1);
    const target = data.nodes.find((node) => node.id === edge.person2);
    const nextEdge = {
      ...edge,
      id: edge._id,
      source: source || edge.person1,
      target: target || edge.person2,
      note: edge.notes,
    };

    setActiveReadTab('graph');
    setIsTimelinePlaying(false);
    setTimelineStep(0);
    setIsTimelineOpen(false);
    setIsTimelineClosing(false);
    setActiveInsightMode(null);
    setInsightStep(0);
    setSelectedNode(null);
    setSelectedEdge(nextEdge);
    if (source && target && typeof source.x === 'number' && typeof target.x === 'number') {
      graphRef.current?.centerAt?.((source.x + target.x) / 2, (source.y + target.y) / 2, 420);
      graphRef.current?.zoom?.(window.innerWidth < 680 ? 1.9 : 2.35, 420);
    }
    setOpen(true);
  };

  const selectReadTab = (tab) => {
    if (tab === 'insights') {
      setIsTimelineOpen(false);
      setIsTimelineClosing(false);
      setIsTimelinePlaying(false);
      setTimelineStep(0);
    }
    setActiveReadTab(tab);
    setOpen(false);
    setSelectedEdge(null);
    setSelectedNode(null);
    setActiveInsightMode(null);
    setInsightStep(0);
  };

  const captureTimelineLayout = useCallback(() => {
    const nextPositions = new Map();
    data.nodes.forEach((node) => {
      const x = node.x ?? node.locationX ?? stableCoordinate(node.id, 'x');
      const y = node.y ?? node.locationY ?? stableCoordinate(node.id, 'y');
      nextPositions.set(node.id, { x, y });
    });
    timelineLayoutRef.current = nextPositions;
  }, [data.nodes]);

  const fitTimelineGraph = useCallback((duration = 520) => {
    window.setTimeout(() => {
      graphRef.current?.zoomToFit?.(duration, graphSize.width > 760 ? 96 : 44);
    }, 120);
  }, [graphSize.width]);

  const startTimeline = useCallback(() => {
    if (!timelineEdges.length) return;
    if (timelineCloseTimerRef.current) {
      window.clearTimeout(timelineCloseTimerRef.current);
      timelineCloseTimerRef.current = null;
    }
    if (!isTimelineActive) {
      captureTimelineLayout();
    }
    setActiveReadTab('graph');
    setOpen(false);
    setSelectedEdge(null);
    setSelectedNode(null);
    setIsTimelineClosing(false);
    setIsTimelineOpen(true);
    setTimelineStep((current) => (isTimelineActive && current < timelineEdges.length ? current : 0));
    setIsTimelinePlaying(true);
    fitTimelineGraph();
  }, [captureTimelineLayout, fitTimelineGraph, isTimelineActive, timelineEdges.length]);

  const pauseTimeline = useCallback(() => {
    setIsTimelinePlaying(false);
  }, []);

  const stepTimeline = useCallback((direction) => {
    if (!timelineEdges.length) return;
    if (timelineCloseTimerRef.current) {
      window.clearTimeout(timelineCloseTimerRef.current);
      timelineCloseTimerRef.current = null;
    }
    if (!isTimelineActive) {
      captureTimelineLayout();
    }
    setActiveReadTab('graph');
    setOpen(false);
    setSelectedEdge(null);
    setSelectedNode(null);
    setIsTimelineClosing(false);
    setIsTimelineOpen(true);
    setIsTimelinePlaying(false);
    setTimelineStep((current) => {
      return Math.max(0, Math.min(timelineEdges.length, current + direction));
    });
    fitTimelineGraph(360);
  }, [captureTimelineLayout, fitTimelineGraph, isTimelineActive, timelineEdges.length]);

  const shareGraph = useCallback(async () => {
    const shareUrl = window.location.href;
    const shareTitle = graphData?.graph?.name ? `${graphData.graph.name} on How You Met Me` : 'How You Met Me';
    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: 'Open this relationship map on How You Met Me.',
          url: shareUrl,
        });
        return;
      }
      await navigator.clipboard?.writeText?.(shareUrl);
    } catch (shareError) {
      if (shareError?.name !== 'AbortError') {
        console.error('Could not share map link', shareError);
      }
    }
  }, [graphData?.graph?.name]);

  const stopTimeline = () => {
    if (timelineCloseTimerRef.current) {
      window.clearTimeout(timelineCloseTimerRef.current);
    }
    setIsTimelineClosing(true);
    setIsTimelinePlaying(false);
    timelineCloseTimerRef.current = window.setTimeout(() => {
      setIsTimelineOpen(false);
      setIsTimelineClosing(false);
      setTimelineStep(0);
      timelineLayoutRef.current = new Map();
      graphRef.current?.zoomToFit?.(420, window.innerWidth < 680 ? 44 : 80);
      timelineCloseTimerRef.current = null;
    }, 380);
  };

  const openInsightVisual = useCallback((mode) => {
    if (mode === 'path' && pathPersonIds.length < 2) return;
    if (mode === 'whatIf' && !whatIfPersonId) return;
    if (timelineCloseTimerRef.current) {
      window.clearTimeout(timelineCloseTimerRef.current);
      timelineCloseTimerRef.current = null;
    }
    setIsTimelineOpen(false);
    setIsTimelineClosing(false);
    setIsTimelinePlaying(false);
    setTimelineStep(0);
    setOpen(false);
    setSelectedEdge(null);
    setSelectedNode(null);
    setActiveReadTab('graph');
    setActiveInsightMode(mode);
    setInsightStep(0);
    window.setTimeout(() => {
      graphRef.current?.zoomToFit?.(560, graphSize.width > 760 ? 96 : 44);
    }, 120);
  }, [graphSize.width, pathPersonIds.length, whatIfPersonId]);

  const openSuperlativeVisual = useCallback((superlativeId) => {
    const card = superlativeCards.find((item) => item.id === superlativeId) || superlativeCards[0];
    if (!card) return;
    if (timelineCloseTimerRef.current) {
      window.clearTimeout(timelineCloseTimerRef.current);
      timelineCloseTimerRef.current = null;
    }
    setSelectedSuperlativeId(card.id);
    setIsTimelineOpen(false);
    setIsTimelineClosing(false);
    setIsTimelinePlaying(false);
    setTimelineStep(0);
    setOpen(false);
    setSelectedEdge(null);
    setSelectedNode(null);
    setActiveReadTab('graph');
    setActiveInsightMode('superlative');
    setInsightStep(0);

    window.setTimeout(() => {
      const highlightedNodes = (card.personIds || [])
        .map((personId) => data.nodes.find((node) => node.id === personId))
        .filter((node) => node && Number.isFinite(node.x) && Number.isFinite(node.y));
      if (!highlightedNodes.length) {
        graphRef.current?.zoomToFit?.(560, graphSize.width > 760 ? 96 : 44);
        return;
      }
      const centerX = highlightedNodes.reduce((sum, node) => sum + node.x, 0) / highlightedNodes.length;
      const centerY = highlightedNodes.reduce((sum, node) => sum + node.y, 0) / highlightedNodes.length;
      graphRef.current?.centerAt?.(centerX, centerY, 520);
      graphRef.current?.zoom?.(highlightedNodes.length > 1 ? (window.innerWidth < 680 ? 1.8 : 2.2) : (window.innerWidth < 680 ? 2.35 : 2.8), 520);
    }, 140);
  }, [data.nodes, graphSize.width, superlativeCards]);

  const closeInsightVisual = useCallback(() => {
    setActiveInsightMode(null);
    setInsightStep(0);
    graphRef.current?.zoomToFit?.(420, window.innerWidth < 680 ? 44 : 80);
  }, []);

  const updateSuggestionField = useCallback((field, value) => {
    if (field === 'suggestedPersonImageURL') {
      setSuggestionImageFailed(false);
    }
    setSuggestionForm((current) => ({
      ...current,
      [field]: value,
    }));
    setSuggestionStatus('');
    setSuggestionError('');
  }, []);

  const updateSuggestionPhoto = useCallback((imageUrl) => {
    setSuggestionImageFailed(false);
    updateSuggestionField('suggestedPersonImageURL', imageUrl || '');
  }, [updateSuggestionField]);

  const submitSuggestion = async (event) => {
    event.preventDefault();
    setSuggestionStatus('');
    setSuggestionError('');
    try {
      const result = await submitGraphSuggestion({
        variables: {
          input: {
            ...suggestionForm,
            graphName,
            suggestedPersonName: suggestionForm.suggestedPersonName.trim(),
            suggestedPersonImageURL: suggestionForm.suggestedPersonImageURL.trim(),
            notes: suggestionForm.notes.trim(),
            submitterName: suggestionForm.submitterName.trim(),
            submitterEmail: suggestionForm.submitterEmail.trim(),
            submitterNote: suggestionForm.submitterNote.trim(),
          },
        },
      });
      const didAutoApprove = result.data?.submitGraphSuggestion?.status === 'approved';
      if (didAutoApprove) {
        await refetchGraph();
      }
      const keptConnection = suggestionForm.connectToPersonId;
      setSuggestionForm({
        suggestedPersonName: '',
        suggestedPersonImageURL: '',
        connectToPersonId: keptConnection,
        dateMet: '',
        category: 'friendship',
        notes: '',
        submitterName: '',
        submitterEmail: '',
        submitterNote: '',
      });
      setSuggestionImageFailed(false);
      setSuggestionStatus(didAutoApprove
        ? 'Added to the map.'
        : 'Thanks. Your suggestion was sent for approval.');
    } catch (submitError) {
      setSuggestionError(submitError.message || 'Could not submit this suggestion.');
    }
  };

  const submitPersonPhotoSuggestion = async ({personId, imageUrl, submitterName = '', submitterEmail = '', submitterNote = ''}) => {
    const targetPerson = idToPerson[personId];
    if (!targetPerson) {
      throw new Error('Choose someone already in this map.');
    }

    const result = await submitGraphSuggestion({
      variables: {
        input: {
          graphName,
          kind: 'photo_update',
          targetPersonId: personId,
          suggestedPersonName: targetPerson.name,
          suggestedPersonImageURL: imageUrl.trim(),
          notes: submitterNote.trim() || `Photo update for ${targetPerson.name}`,
          submitterName: submitterName.trim(),
          submitterEmail: submitterEmail.trim(),
          submitterNote: submitterNote.trim(),
        },
      },
    });
    const didAutoApprove = result.data?.submitGraphSuggestion?.status === 'approved';
    if (didAutoApprove) {
      await refetchGraph();
    }
    return { approved: didAutoApprove };
  };

  const submitPersonStorySuggestion = async ({
    personId,
    title = '',
    story = '',
    storyDate = '',
    taggedPersonIds = [],
    submitterName = '',
    submitterEmail = '',
    submitterNote = '',
  }) => {
    const targetPerson = idToPerson[personId];
    if (!targetPerson) {
      throw new Error('Choose someone already in this map.');
    }

    const result = await submitGraphSuggestion({
      variables: {
        input: {
          graphName,
          kind: 'person_story',
          targetPersonId: personId,
          suggestedPersonName: targetPerson.name,
          storyTitle: title.trim(),
          storyText: story.trim(),
          storyDate: storyDate || null,
          taggedPersonIds,
          notes: story.trim(),
          submitterName: submitterName.trim(),
          submitterEmail: submitterEmail.trim(),
          submitterNote: submitterNote.trim(),
        },
      },
    });

    const didAutoApprove = result.data?.submitGraphSuggestion?.status === 'approved';
    if (didAutoApprove) {
      await refetchGraph();
    }
    return { approved: didAutoApprove };
  };

  useEffect(() => () => {
    if (timelineCloseTimerRef.current) {
      window.clearTimeout(timelineCloseTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const handleTimelineKeydown = (event) => {
      if (activeReadTab !== 'graph' || !timelineEdges.length || event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target;
      const targetTagName = target?.tagName?.toLowerCase();
      const isTypingTarget = target?.isContentEditable
        || targetTagName === 'input'
        || targetTagName === 'textarea'
        || targetTagName === 'select';
      if (isTypingTarget) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepTimeline(-1);
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepTimeline(1);
        return;
      }

      if ((event.key === ' ' || event.code === 'Space') && !event.repeat) {
        event.preventDefault();
        if (isTimelinePlaying) {
          pauseTimeline();
        } else {
          startTimeline();
        }
      }
    };

    window.addEventListener('keydown', handleTimelineKeydown);
    return () => window.removeEventListener('keydown', handleTimelineKeydown);
  }, [activeReadTab, isTimelinePlaying, pauseTimeline, startTimeline, stepTimeline, timelineEdges.length]);

  const graphCanvasWidth = (isTimelineActive || isInsightVisualActive) && graphSize.width > 760
    ? Math.max(320, graphSize.width - 392)
    : graphSize.width;
  const readTabIndex = activeReadTab === 'insights' ? 1 : activeReadTab === 'add' ? 2 : 0;

  const isWeddingGraph = graphType === 'wedding';

  return (
    <div className={`graph-read-page graph-read-page--${graphType}`}>
        <nav className="graph-read-tabs" style={{ '--active-read-tab': readTabIndex }} aria-label="Read-only map sections">
          <span className="graph-read-tab-indicator" aria-hidden="true" />
          <button
            type="button"
            className={`graph-read-tab${activeReadTab === 'graph' ? ' graph-read-tab--active' : ''}`}
            onClick={() => selectReadTab('graph')}
          >
            Map
          </button>
          <button
            type="button"
            className={`graph-read-tab${activeReadTab === 'insights' ? ' graph-read-tab--active' : ''}`}
            onClick={() => selectReadTab('insights')}
          >
            Insights
          </button>
          <button
            type="button"
            className={`graph-read-tab${activeReadTab === 'add' ? ' graph-read-tab--active' : ''}`}
            onClick={() => selectReadTab('add')}
          >
            Add Person
          </button>
        </nav>
        {error && (
          <div className="graph-message graph-message--error">
            {isGraphNotFound
              ? 'No memory map exists for this URL name.'
              : 'Could not load the memory map. Refresh the page to try again.'}
          </div>
        )}
        {loading && (
          <div className="graph-message">
            Loading map...
          </div>
        )}
        {!loading && !error && graphData?.people?.length === 0 && (
          <div className="graph-message">
            No people are in the map yet.
          </div>
        )}
        {<EdgeDrawer
            selectedEdge={selectedEdge}
            onClose={() => setSelectedEdge(null)}
            person1={idToPerson[selectedEdge?.person1]}
            person2={idToPerson[selectedEdge?.person2]}
            toggleDrawer={toggleDrawer}
            onPersonClick={(person) => {
                setOpen(false);
                const node = data.nodes.find((item) => item.id === person._id);
                graphRef.current.centerAt(
                  node?.x ?? person.locationX ?? stableCoordinate(person._id, 'x'),
                  node?.y ?? person.locationY ?? stableCoordinate(person._id, 'y'),
                  500
                )

                setTimeout(() => {
                    setSelectedEdge(null);
                    setSelectedNode(person);
                    setOpen(true);
                },200);
            }}
            open={Boolean(open && selectedEdge)}
        />}
        {<PersonDrawer
            personSelected={selectedNode}
            onEdgeClick={(edge) => {
                setOpen(false);
                setTimeout(() => {
                    setSelectedNode(null);
                    setSelectedEdge(edge);
                    setOpen(true);
                },200);
            }}
            onClose={() => setSelectedNode(null)}
            idToPerson={idToPerson}
            toggleDrawer={toggleDrawer}
            open={Boolean(open && selectedNode)}
            edges={personToEdges[selectedNode?._id]}
            onPhotoSuggestionSubmit={submitPersonPhotoSuggestion}
            isSubmittingPhotoSuggestion={isSubmittingSuggestion}
            approvedStories={storiesByPerson[selectedNode?._id] || []}
            people={analyticsPeople}
            onStorySuggestionSubmit={submitPersonStorySuggestion}
            isSubmittingStorySuggestion={isSubmittingSuggestion}
        />}
        {activeReadTab === 'graph' && !isTimelineActive && !open && !selectedEdge && !selectedNode && Boolean(timelineEdges.length) && (
          <div className="read-bottom-actions">
            <button
              type="button"
              className="read-timeline-launcher"
              onClick={startTimeline}
            >
              <span>Relationship History</span>
              <strong>Play timeline</strong>
            </button>
            <button
              type="button"
              className="read-mobile-share"
              onClick={shareGraph}
              aria-label="Share map"
              title="Share map"
            >
              <ShareIcon fontSize="small" />
            </button>
          </div>
        )}
        {activeReadTab === 'graph' && isTimelineActive && (
          <aside className={`read-timeline-player${isTimelineClosing ? ' read-timeline-player--closing' : ''}`} aria-label="Relationship history playback">
            <div className="read-timeline-player-header">
              <span>Relationship History</span>
              <strong>{graphData?.graph?.name || 'How You Met Me'}</strong>
            </div>
            <div className="read-timeline-summary">
              <strong>{timelineEdgeCount}/{timelineEdges.length} friendships formed</strong>
              <p>{currentTimelineEdge ? getFriendshipLabel(currentTimelineEdge) : 'All people are in place. Press play to reveal the first story.'}</p>
              <small>{timelineTreeCount} disjointed tree{timelineTreeCount === 1 ? '' : 's'} visible</small>
            </div>
            <div className="read-history-track" aria-hidden="true">
              <span style={{ width: `${timelineEdges.length ? (timelineEdgeCount / timelineEdges.length) * 100 : 0}%` }} />
            </div>
            <div className="read-timeline-list" ref={timelineListRef}>
              {visibleTimelineEdges.length ? (
                visibleTimelineEdges.map((edge, index) => (
                  <button
                    key={edge._id}
                    type="button"
                    className={`read-timeline-list-item${index === visibleTimelineEdges.length - 1 ? ' read-timeline-list-item--current' : ''}`}
                    onClick={() => handleEdgeClick({
                      ...edge,
                      id: edge._id,
                      source: data.nodes.find((node) => node.id === edge.person1) || edge.person1,
                      target: data.nodes.find((node) => node.id === edge.person2) || edge.person2,
                      note: edge.notes,
                    })}
                  >
                      <span>{index + 1}</span>
                      <strong>{getFriendshipLabel(edge)}</strong>
                      <small>{timelineTreeCountsByStep[index]} disjointed tree{timelineTreeCountsByStep[index] === 1 ? '' : 's'} - {edge.notes || 'No story note yet.'}</small>
                  </button>
                ))
              ) : (
                <div className="read-timeline-empty">
                  The first connection will appear here.
                </div>
              )}
            </div>
            <div className="read-timeline-actions">
              <button
                type="button"
                onClick={() => stepTimeline(-1)}
                disabled={timelineEdgeCount <= 0}
                aria-label="Back one step"
                title="Back one step"
              >
                <SkipPreviousIcon fontSize="small" />
              </button>
              <button
                type="button"
                onClick={isTimelinePlaying ? pauseTimeline : startTimeline}
                aria-label={isTimelinePlaying ? 'Pause timeline' : 'Play timeline'}
                title={isTimelinePlaying ? 'Pause timeline' : 'Play timeline'}
              >
                {isTimelinePlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
              </button>
              <button
                type="button"
                onClick={() => stepTimeline(1)}
                disabled={timelineEdgeCount >= timelineEdges.length}
                aria-label="Forward one step"
                title="Forward one step"
              >
                <SkipNextIcon fontSize="small" />
              </button>
              <button
                type="button"
                onClick={stopTimeline}
                aria-label="Stop timeline"
                title="Stop timeline"
              >
                <StopIcon fontSize="small" />
              </button>
            </div>
          </aside>
        )}
        {activeReadTab === 'graph' && activeInsightMode && (
          <aside className={`read-insight-visual-panel read-insight-visual-panel--${activeInsightMode}`} aria-label="Insight visualization">
            <div className="read-insight-visual-header">
              <span>
                {activeInsightMode === 'path'
                  ? 'How am I connected?'
                  : activeInsightMode === 'whatIf'
                    ? 'What if?'
                    : 'Superlatives'}
              </span>
              <button type="button" onClick={closeInsightVisual} aria-label="Close insight visualization" title="Close">
                <CloseIcon fontSize="small" />
              </button>
            </div>
            {activeInsightMode === 'path' ? (
              <>
                <strong>{pathPersonIds.length ? `${Math.max(0, pathPersonIds.length - 1)} degrees of separation` : 'No path found'}</strong>
                <p>
                  {pathPersonIds.length
                    ? pathPersonIds.map(getPersonName).join(' -> ')
                    : 'Try a different pair to find a visible connection.'}
                </p>
                {pathUsesSmallWorldConnection && (
                  <p className="read-path-small-world-note">
                    Includes a small world connection.
                  </p>
                )}
                <div className="read-history-track" aria-hidden="true">
                  <span style={{ width: `${pathEdges.length ? (Math.min(insightStep, pathEdges.length) / pathEdges.length) * 100 : 0}%` }} />
                </div>
                <div className="read-insight-visual-list" ref={insightListRef}>
                  {pathPersonIds.map((personId, index) => {
                    const incomingEdge = index > 0 ? pathEdges[index - 1] : null;
                    const isSmallWorldStep = incomingEdge?._id && smallWorldPathEdgeIds.has(incomingEdge._id);
                    return (
                      <div
                        key={personId}
                        className={`read-insight-visual-step${index <= insightStep ? ' read-insight-visual-step--active' : ''}${isSmallWorldStep ? ' read-insight-visual-step--small-world' : ''}`}
                      >
                        <span>{index + 1}</span>
                        <strong>{getPersonName(personId)}</strong>
                        <small>
                          {index === 0 ? 'Start' : `Step ${index}`}
                          {isSmallWorldStep && <em className="read-small-world-badge">Small world connection</em>}
                        </small>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : activeInsightMode === 'whatIf' ? (
              <>
                <strong>Remove {getPersonName(whatIfPersonId)}</strong>
                <p>
                  {whatIfResult
                    ? `Watch the group try to heal after ${getPersonName(whatIfPersonId)} disappears from the network.`
                    : 'Choose a person to simulate impact.'}
                </p>
                {whatIfSmallWorldRescue && (
                  <p className="read-path-small-world-note">
                    A small world connection keeps a section connected that would otherwise split off.
                  </p>
                )}
                <div className="read-insight-visual-list" ref={insightListRef}>
                  {whatIfStorySteps.map((step, index) => (
                    <div
                      key={`${step.title}-${index}`}
                      className={`read-insight-visual-step read-insight-visual-step--${step.tone}${index <= insightStep ? ' read-insight-visual-step--active' : ''}`}
                    >
                      <span>{index + 1}</span>
                      <strong>{step.title}</strong>
                      <small>{step.detail}</small>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <strong>{selectedSuperlativeCard?.title || 'Map superlatives'}</strong>
                <p>
                  {selectedSuperlativeCard
                    ? 'Click any card to reveal the answer and highlight it on the map.'
                    : 'No superlatives are ready yet.'}
                </p>
                <div className="read-superlative-list read-superlative-list--panel">
                  {superlativeCards.map((card) => {
                    const isSelected = selectedSuperlativeCard?.id === card.id;
                    return (
                      <button
                        key={card.id}
                        type="button"
                        className={`read-superlative-card${isSelected ? ' read-superlative-card--revealed' : ''}`}
                        style={{ '--superlative-color': card.accent }}
                        onClick={() => openSuperlativeVisual(card.id)}
                      >
                        <span>{card.title}</span>
                        <strong>{card.teaser}</strong>
                        <p className="read-superlative-card-prompt">Tap to reveal the answer.</p>
                        <p className="read-superlative-card-answer">{card.answer}</p>
                        <small>{card.detail}</small>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </aside>
        )}
      {activeReadTab === 'add' && (
        <section className="graph-suggestion-page" aria-label="Suggest a person">
          <div className="graph-suggestion-shell">
            <div className="graph-suggestion-hero">
              <span>Help grow the map</span>
              <h1>Add someone you know</h1>
              <p>
                Suggest a person and the story of how they connect. Kevin will review it before it appears on the public map.
              </p>
            </div>
            <form className="graph-suggestion-form" onSubmit={submitSuggestion}>
              <div className="graph-suggestion-preview">
                <div className="graph-suggestion-avatar">
                  {suggestionForm.suggestedPersonImageURL && !suggestionImageFailed ? (
                    <img
                      src={suggestionForm.suggestedPersonImageURL}
                      alt=""
                      onError={() => setSuggestionImageFailed(true)}
                    />
                  ) : (
                    <span>{suggestionForm.suggestedPersonName.trim().charAt(0) || '+'}</span>
                  )}
                </div>
                <div>
                  <strong>{suggestionForm.suggestedPersonName || 'New person'}</strong>
                  <small>
                    connects to {getPersonName(suggestionForm.connectToPersonId)}
                  </small>
                </div>
              </div>

              <div className="graph-suggestion-grid">
                <label>
                  <span>Person name</span>
                  <input
                    value={suggestionForm.suggestedPersonName}
                    onChange={(event) => updateSuggestionField('suggestedPersonName', event.target.value)}
                    placeholder="Who should be added?"
                    required
                  />
                </label>
                <div className="graph-suggestion-photo-field">
                  <span>Photo</span>
                  <input
                    value={suggestionForm.suggestedPersonImageURL}
                    onChange={(event) => updateSuggestionField('suggestedPersonImageURL', event.target.value)}
                    placeholder="Paste a photo URL or upload"
                  />
                  <CloudinaryUpload
                    imageUrl={suggestionForm.suggestedPersonImageURL}
                    setImageUrl={updateSuggestionPhoto}
                  />
                  {suggestionImageFailed && (
                    <small>That photo could not load. Try another URL or upload a new photo.</small>
                  )}
                </div>
                <SearchableSelect
                  label="They know"
                  options={personSelectOptions}
                  value={suggestionForm.connectToPersonId}
                  onChange={(nextValue) => updateSuggestionField('connectToPersonId', nextValue)}
                  placeholder="Search people"
                />
                <label>
                  <span>When did they meet?</span>
                  <input
                    type="date"
                    value={suggestionForm.dateMet}
                    onChange={(event) => updateSuggestionField('dateMet', event.target.value)}
                    required
                  />
                </label>
                <SearchableSelect
                  label="Category"
                  options={categorySelectOptions}
                  value={suggestionForm.category}
                  onChange={(nextValue) => updateSuggestionField('category', nextValue)}
                  placeholder="Search categories"
                />
                <label>
                  <span>Your name</span>
                  <input
                    value={suggestionForm.submitterName}
                    onChange={(event) => updateSuggestionField('submitterName', event.target.value)}
                    placeholder="Optional"
                  />
                </label>
              </div>

              <label className="graph-suggestion-wide-field">
                <span>How did they meet?</span>
                <textarea
                  value={suggestionForm.notes}
                  onChange={(event) => updateSuggestionField('notes', event.target.value)}
                  placeholder="Tell the story in a few sentences."
                  required
                />
              </label>

              <div className="graph-suggestion-grid">
                <label>
                  <span>Your email</span>
                  <input
                    type="email"
                    value={suggestionForm.submitterEmail}
                    onChange={(event) => updateSuggestionField('submitterEmail', event.target.value)}
                    placeholder="Optional"
                  />
                </label>
                <label>
                  <span>Note for Kevin</span>
                  <input
                    value={suggestionForm.submitterNote}
                    onChange={(event) => updateSuggestionField('submitterNote', event.target.value)}
                    placeholder="Optional"
                  />
                </label>
              </div>

              {suggestionStatus && <div className="graph-suggestion-status graph-suggestion-status--success">{suggestionStatus}</div>}
              {suggestionError && <div className="graph-suggestion-status graph-suggestion-status--error">{suggestionError}</div>}

              <button
                type="submit"
                className="graph-suggestion-submit"
                disabled={isSubmittingSuggestion || !suggestionForm.suggestedPersonName.trim() || !suggestionForm.connectToPersonId || !suggestionForm.dateMet || !suggestionForm.notes.trim()}
              >
                {isSubmittingSuggestion
                  ? (isEditorSubmitting ? 'Adding...' : 'Sending...')
                  : (isEditorSubmitting ? 'Add' : 'Send for approval')}
              </button>
            </form>
          </div>
        </section>
      )}
      {activeReadTab === 'insights' && (
        <section className="graph-read-insights" aria-label="Map insights">
          <div className="graph-read-insights-header">
            <div>
              <span>Discovery & insights</span>
              <h1>{graphData?.graph?.name || 'How You Met Me'}</h1>
            </div>
            <strong>{getGraphTypeLabel(graphType)} - {analyticsPeople.length} people</strong>
          </div>

          <div className="graph-read-insights-grid">
            <article className="read-insight-card read-insight-card--wide">
              <div className="read-insight-cta-card">
                <div>
                  <span>Path finder</span>
                  <h2>How am I connected?</h2>
                  <p>
                    Pick any two people and watch the map animate the shortest relationship path between them, including any small-world shortcuts.
                  </p>
                </div>
                <button
                  type="button"
                  className="read-insight-action"
                  onClick={() => openInsightVisual('path')}
                  disabled={pathPersonIds.length < 2}
                >
                  Trace the thread
                </button>
              </div>
              <div className="read-insight-controls">
                <SearchableSelect
                  label="From"
                  options={personSelectOptions}
                  value={pathStartPersonId}
                  onChange={setPathStartPersonId}
                  placeholder="Search people"
                />
                <SearchableSelect
                  label="To"
                  options={personSelectOptions}
                  value={pathEndPersonId}
                  onChange={setPathEndPersonId}
                  placeholder="Search people"
                />
              </div>
            </article>

            <article className="read-insight-card read-insight-card--wide">
              <div className="read-insight-title-row read-insight-cta-card">
                <div>
                  <span>Superlatives</span>
                  <h2>Best of the map</h2>
                  <p>
                    Reveal playful awards like most central person, oldest non-family bond, and the couple whose breakup would shake the map most.
                  </p>
                </div>
                <button
                  type="button"
                  className="read-insight-action"
                  onClick={() => openSuperlativeVisual(selectedSuperlativeCard?.id || superlativeCards[0]?.id)}
                  disabled={!superlativeCards.length}
                >
                  Crown the winner
                </button>
              </div>
              <div className="read-superlative-list">
                {superlativeCards.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    className="read-superlative-card"
                    style={{ '--superlative-color': card.accent }}
                    onClick={() => openSuperlativeVisual(card.id)}
                  >
                    <span>{card.title}</span>
                    <strong>{card.teaser}</strong>
                    <p className="read-superlative-card-prompt">Click to reveal on the map.</p>
                    <p className="read-superlative-card-answer">{card.answer}</p>
                    <small>{card.detail}</small>
                  </button>
                ))}
              </div>
            </article>
            <article className="read-insight-card">
              <span>Average distance</span>
              <strong>{graphAnalytics.averageDistance ? graphAnalytics.averageDistance.toFixed(2) : '0'} degrees</strong>
            </article>

            <article className="read-insight-card read-insight-card--wide">
              <h2>Degrees of separation</h2>
              <p>
                {graphAnalytics.degreeDistribution.length
                  ? graphAnalytics.degreeDistribution.map(([degree, count]) => `${degree}: ${count}`).join(' - ')
                  : 'No connected pairs yet.'}
              </p>
            </article>

            <article className="read-insight-card read-insight-card--wide">
              <h2>People who introduced the most others</h2>
              <p>
                {graphAnalytics.topIntroducers.length
                  ? graphAnalytics.topIntroducers.slice(0, 6).map((item) => `${getPersonName(item.personId)} (${item.count})`).join(' - ')
                  : 'No introduction pattern yet.'}
              </p>
            </article>

            <article className="read-insight-card read-insight-card--wide">
              <h2>Small world connections</h2>
              {smallWorldEdges.length ? (
                <div className="small-world-list small-world-list--insights">
                  {smallWorldEdges.slice(0, 8).map((edge) => (
                    <button
                      key={edge._id}
                      type="button"
                      className="small-world-item"
                      onClick={() => openSmallWorldEdge(edge)}
                    >
                      <span>{getEdgePersonLabel(edge)}</span>
                      <small>{formatDate(edge.dateMet)}{edge.notes ? ` • ${edge.notes}` : ''}</small>
                    </button>
                  ))}
                </div>
              ) : (
                <p>No extra random connections outside the original chain yet.</p>
              )}
            </article>

            <article className="read-insight-card read-insight-card--wide">
              <h2>Hidden connectors</h2>
              <p>
                {graphAnalytics.hiddenConnectors.length
                  ? graphAnalytics.hiddenConnectors.slice(0, 6).map((item) => getPersonName(item.personId)).join(' - ')
                  : 'No quiet bridge people detected yet.'}
              </p>
            </article>

            <article className="read-insight-card read-insight-card--wide">
              <div className="read-insight-cta-card">
                <div>
                  <span>Simulation</span>
                  <h2>What if this person never joined?</h2>
                  <p>
                    Temporarily remove someone from the network and see which groups split apart, plus whether small-world links keep people connected.
                  </p>
                </div>
                <button
                  type="button"
                  className="read-insight-action"
                  onClick={() => openInsightVisual('whatIf')}
                  disabled={!whatIfPersonId}
                >
                  Spin the what-if
                </button>
              </div>
              <div className="read-insight-controls read-insight-controls--single">
                <SearchableSelect
                  label="Person"
                  options={personSelectOptions}
                  value={whatIfPersonId}
                  onChange={setWhatIfPersonId}
                  placeholder="Search people"
                />
              </div>
            </article>
          </div>
        </section>
      )}
      <div className={`graph-read-container${activeReadTab === 'graph' ? '' : ' graph-read-container--hidden'}`}>
        <ForceGraph2D
          ref={graphRef}
          width={graphCanvasWidth}
          height={graphSize.height}
          cooldownTicks={isTimelineActive ? 0 : 100}
          warmupTicks={isTimelineActive ? 0 : 80}
          engineOptions={{ worker: true }}
          onEngineStop={handleEngineStop}
          graphData={displayedData}
          backgroundColor={isWeddingGraph ? '#fbf7f0' : '#050505'}
          nodeLabel={(node) => node.name}
          onNodeClick={(node) => handleNodeClick(node)}
          onLinkClick={(link) => handleEdgeClick(link)}
          onBackgroundClick={() => {
            setOpen(false);
            setSelectedEdge(null);
            setSelectedNode(null);
          }}
          enableNodeDrag={false}
            nodeCanvasObject={(node, ctx, globalScale) => {
            idToNode[node._id] = node;
            const isSelected = selectedNode?.id === node.id || selectedNode?._id === node._id;
            const isPathMode = isPathInsightActive;
            const isWhatIfMode = isWhatIfInsightActive;
            const isSuperlativeMode = isSuperlativeInsightActive;
            const isPathNode = pathNodeIds.has(node.id);
            const isVisiblePathNode = visiblePathNodeIds.has(node.id);
            const isSuperlativeNode = selectedSuperlativePersonIds.has(node.id);
            const isRemovedNode = isWhatIfMode && node.id === whatIfPersonId;
            const componentIndex = whatIfComponentByPersonId.get(node.id);
            const shouldDimForPath = isPathMode && !isPathNode;
            const shouldDimForWhatIf = isWhatIfMode && insightStep >= 2 && !isRemovedNode && componentIndex === undefined;
            const shouldDimForSuperlative = isSuperlativeMode && !isSuperlativeNode;
            const pulse = 1 + Math.sin(Date.now() / 220) * 0.14;
            const size = Math.max(20, GRAPH_NODE_SIZE / Math.sqrt(Math.max(globalScale, 0.85)));
            ctx.save();
            ctx.globalAlpha = shouldDimForPath || shouldDimForWhatIf || shouldDimForSuperlative ? 0.22 : 1;
            if (isSelected || isVisiblePathNode || isRemovedNode || isSuperlativeNode) {
              ctx.beginPath();
              ctx.arc(node.x, node.y, (size / 2 + 5 / globalScale) * (isRemovedNode || isVisiblePathNode || isSuperlativeNode ? pulse : 1), 0, 2 * Math.PI);
                ctx.strokeStyle = isRemovedNode
                  ? '#ff3b5f'
                  : isSuperlativeNode
                    ? selectedSuperlativeCard?.accent || (isWeddingGraph ? '#6d4b42' : '#ffffff')
                  : isVisiblePathNode
                    ? (isWeddingGraph ? '#9d665d' : '#00d5ff')
                    : (isWeddingGraph ? '#6d4b42' : '#ffffff');
              ctx.lineWidth = (isRemovedNode || isVisiblePathNode || isSuperlativeNode ? 3.2 : 2.4) / globalScale;
              ctx.stroke();
              ctx.closePath();
            }
            ctx.beginPath();
            ctx.arc(node.x, node.y, size / 2, 0, 2 * Math.PI);
            ctx.fillStyle = isRemovedNode
              ? '#ff3b5f'
              : isSuperlativeNode
                ? selectedSuperlativeCard?.accent || getColor(node.id)
              : isWhatIfMode && insightStep >= 2 && componentIndex !== undefined
                ? ['#00d5ff', '#ff2d95', '#ffd60a', '#32ff6a', '#8b5cf6'][componentIndex % 5]
                : getColor(node.id);
            ctx.fill();
            ctx.closePath();

            const imgSize = size - 2;
            const img = getPersonPhotoImage(node, {
              globalScale,
              onLoad: scheduleGraphRefresh,
            });
            drawNodePhoto(ctx, node, img, imgSize, getColor(node.id));
            ctx.restore();
          }}
          linkLabel={(link) => link.note?.length > 20 ? link.note.slice(0, 20) + '...' : link.note}
          linkDirectionalArrowLength={0}
          linkCanvasObject={(link, ctx, globalScale) => {
                const isSelected = selectedEdge?.id === link.id || selectedEdge?._id === link._id;
                const linkId = link.id || link._id;
                const isPathMode = isPathInsightActive;
                const isWhatIfMode = isWhatIfInsightActive;
                const isSuperlativeMode = isSuperlativeInsightActive;
                const isPathLink = pathEdgeIds.has(linkId);
                const isVisiblePathLink = visiblePathEdgeIds.has(linkId);
                const isSuperlativeLink = selectedSuperlativeEdgeIds.has(linkId);
                const isRemovedIncidentLink = isWhatIfMode && whatIfIncidentEdgeIds.has(linkId);
                const shouldDim = (isPathMode && !isPathLink)
                  || (isPathMode && isPathLink && !isVisiblePathLink)
                  || (isWhatIfMode && insightStep >= 1 && isRemovedIncidentLink)
                  || (isSuperlativeMode && !isSuperlativeLink);
                ctx.beginPath();
                ctx.save();
                ctx.globalAlpha = shouldDim ? 0.16 : 1;
                if (isPathLink && !isVisiblePathLink) {
                  ctx.setLineDash([6 / globalScale, 6 / globalScale]);
                }
                ctx.strokeStyle = isRemovedIncidentLink
                  ? '#ff3b5f'
                  : isSuperlativeLink
                    ? selectedSuperlativeCard?.accent || '#ffffff'
                  : isVisiblePathLink
                    ? (isWeddingGraph ? '#9d665d' : '#00d5ff')
                    : isSelected
                      ? (isWeddingGraph ? '#6d4b42' : '#ffffff')
                      : getEdgeCategoryColor(link.category);
                ctx.lineWidth = isVisiblePathLink || isSuperlativeLink ? 4 / globalScale : isSelected ? 3 / globalScale : 1.7 / globalScale;
                ctx.moveTo(link.source.x, link.source.y);
                ctx.lineTo(link.target.x, link.target.y);
                ctx.stroke();
                ctx.restore();
          }}
        />
      </div>
  

    </div>
  );
}
